"use client";

import { Dispatch, SetStateAction, useState } from "react";
import {
  LOCALE,
  feePercentage,
  formatPrice,
  formatPriceWithCurrency,
  steps,
  taxPercentage,
} from "../index";
import { ScrollArea, Separator } from "../../..";
import { cn } from "@punchup/utils/cn";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";

import { CartItem } from "./types";
import { ComedianNames } from "../ComedianNames";
import Image from "next/image";
import { ShowInfo } from "@punchup/data/legacy";
import { Stepper } from "../../common";
import dayjs from "dayjs";

interface Props {
  show: ShowInfo;
  comedianNames: string[];
  cart: CartItem[];
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  buttonLabel: string;
  buttonDisabled: boolean;
  onButtonClick: () => void;
}

export const CheckoutModalLayout = ({
  show,
  comedianNames,
  cart,
  activeStep,
  setActiveStep,
  title,
  subtitle,
  children,
  buttonLabel,
  buttonDisabled,
  onButtonClick,
}: Props) => {
  const [errors, setErrors] = useState<{ purchase?: string }>({});

  const ticketCount = cart.reduce((sum, t) => sum + t.quantity, 0);
  const subtotal = cart.reduce((sum, t) => sum + t.price * t.quantity, 0);
  const fees = cart.reduce(
    (sum, t) => sum + t.price * feePercentage * t.quantity,
    0
  );
  const taxes = Math.round((subtotal + fees) * taxPercentage * 100) / 100;
  const total = subtotal + fees + taxes;

  return (
    <>
      <div className="px-5 md:px-0 pt-4 md:pt-0 flex flex-col gap-8 md:gap-0 md:flex-row md:w-[1078px] h-[70dvh] overflow-hidden md:h-[814px]">
        {/* left column: stepper, title, promo codes, and ticket type options */}
        <div className="flex-1 flex flex-col h-full">
          <Stepper
            steps={steps}
            activeStepIndex={activeStep}
            onStepClick={setActiveStep}
            className="pb-8 md:px-16 md:pt-16"
          />
          <TitleAndSubtitle
            title={title}
            subtitle={subtitle}
            className="pb-4 md:px-16"
          />
          <ScrollArea className="flex-1 md:mx-4">
            <div className="flex flex-col flex-1 gap-8 md:px-12 md:pt-4 pb-8">
              {children}
            </div>
          </ScrollArea>
          <PurchaseButton
            onClick={onButtonClick}
            disabled={buttonDisabled}
            containerClassName="hidden md:flex"
            buttonLabel={buttonLabel}
          />
        </div>
        {/* right column: the cart/order summary */}
        {/* NOTE: hidden on mobile */}
        {/* TODO: show the cart/order summary on mobile */}
        <div className="hidden md:flex rounded-xl md:rounded-none w-full md:w-[366px] bg-[#F7F7F9] px-4 py-6 md:px-10 md:py-16 flex-col gap-6">
          <div className="flex flex-col gap-3">
            {show.posterImg ? (
              <Image
                src={getCDNImageURL(show.posterImg)}
                alt="Show Poster"
                className="w-full h-80 rounded-xl"
                width={320}
                height={320}
              />
            ) : null}
            <div className="flex flex-col gap-1">
              {show.title ? (
                <p className="text-primary-blue font-bold text-lg">
                  {show.title}
                </p>
              ) : (
                <ComedianNames comedianNames={comedianNames} />
              )}
              <p className="text-secondary-blue text-[14px]">
                {dayjs(show.datetime)
                  .locale(LOCALE)
                  .format("dddd MMMM D [at] h:ma")}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              {cart
                .filter((item) => item.quantity > 0)
                .map((item) => (
                  <div key={item.id} className="flex flex-row justify-between">
                    <div className="flex flex-row gap-1">
                      <p className="text-primary-blue text-[14px] font-bold">
                        {item.quantity} x{" "}
                      </p>
                      <p className="text-primary-blue text-[14px] font-medium">
                        {item.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-primary-blue text-[14px]">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between text-[14px]">
                <p className="font-medium">Subtotal</p>
                <p>{formatPrice(subtotal)}</p>
              </div>
              <div className="flex flex-row justify-between text-[14px]">
                <p className="font-medium">Fees</p>
                <p>{formatPrice(fees)}</p>
              </div>
              <div className="flex flex-row justify-between text-[14px]">
                <p className="font-medium">Taxes</p>
                <p>{formatPrice(taxes)}</p>
              </div>
            </div>
            <Separator className="bg-[#C7C8C9] mb-1" />
            <div className="flex flex-row justify-between">
              <p className="text-primary-blue text-[20px] font-bold">Total</p>
              <p className="text-primary-blue text-[20px] font-bold">
                {formatPriceWithCurrency(total)}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* mobile "Purchase" button anchored to the bottom of the screen */}
      <PurchaseButton
        buttonLabel={buttonLabel}
        onClick={onButtonClick}
        disabled={buttonDisabled}
        buttonClassName="w-full py-3"
        containerClassName="px-4 py-3 bottom-0 left-0 right-0 md:hidden"
      >
        <div className="flex flex-col gap-2">
          {errors.purchase && (
            <p className="text-red-500 text-[14px]">{errors.purchase}</p>
          )}
          <div className="flex flex-row justify-between w-full pb-2">
            <p className="font-semibold text-primary-blue">
              Tickets: {ticketCount}
            </p>
            <p className="font-semibold text-primary-blue">
              {formatPriceWithCurrency(total)}
            </p>
          </div>
        </div>
      </PurchaseButton>
    </>
  );
};

const PurchaseButton = ({
  children,
  disabled,
  buttonClassName,
  containerClassName,
  onClick,
  buttonLabel,
}: {
  children?: React.ReactNode;
  disabled?: boolean;
  buttonClassName?: string;
  containerClassName?: string;
  onClick: () => void;
  buttonLabel: string;
}) => {
  return (
    <div
      className={cn(
        "sticky bottom-0 bg-white rounded-b-md w-full flex-row justify-center items-center py-[18px] border-t border-gray-100 z-10",
        "shadow-[0_-2px_10px_0px_rgba(205,205,205,0.4)]",
        containerClassName
      )}
    >
      {children}
      <button
        className={cn(
          "btn px-20 py-2.5 text-[14px] leading-[26px] font-semibold transition-opacity ease-in-out duration-200",
          disabled && "opacity-50",
          buttonClassName
        )}
        disabled={disabled}
        onClick={onClick}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

const TitleAndSubtitle = ({
  title,
  subtitle,
  className,
}: {
  title: string;
  subtitle: string;
  className?: string;
}) => {
  return (
    <div className={cn("flex flex-col gap-1 md:gap-2", className)}>
      <h1 className="text-primary-blue text-xl md:text-2xl font-semibold md:font-bold font-sans normal-case">
        {title}
      </h1>
      <h2 className="text-secondary-blue text-[14px]">{subtitle}</h2>
    </div>
  );
};
